import Checkbox from '@components/Checkbox';
import Column from '@components/Column';
import Dropdown from '@components/Dropdown';
import ImageUploader from '@components/ImageUploader';
import Row from '@components/Row';
import Text from '@components/Text';
import TextField from '@components/TextField';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import useTermCategories from '@hooks/term/useTermCategories';
import { Link } from '@stackflow/link';
import { useTranslation } from 'react-i18next';

import useInquiryForm from './hooks/useInquiryForm';
import useInquiryTypes from './hooks/useInquiryTypes';
import useUpdateInquiryType from './hooks/useUpdateInquiryType';

interface Props {
    values: ReturnType<typeof useInquiryForm>;
}

function InquiryFormContents({ values }:Props) {
    const {
        setInquiryTypeId, content, setContent, title, setTitle, inquiryTypeId,
        privacy, setPrivacy, fileUrls, setFileUrls,
    } = values;
    const { t } = useTranslation('inquiry');

    const inquiryTypes = useInquiryTypes();
    const termCategories = useTermCategories('INQUIRY');
    const handleUpdateType = useUpdateInquiryType({
        setContent, setInquiryTypeId, inquiryTypes,
    });

    return (
        <Container fullWidth spacing={20} >
            <Column spacing={12} fullWidth>
                <Text color={colors.gray7} fontWeight='bold' size='lg'>{t('form.type')}</Text>
                <Dropdown
                    listTitle={t('form.typeListTitle')}
                    value={inquiryTypeId}
                    onChange={handleUpdateType}
                    placeholder={t('form.typePlaceholder')}
                    options={inquiryTypes?.map((inquiryType) => ({
                        value: inquiryType.inquiryTypeId, label: inquiryType.name,
                    }))} />
            </Column>
            <Column spacing={12} fullWidth>
                <Text color={colors.gray7} fontWeight='bold' size='lg'>{t('form.title')}</Text>
                <TextField placeholder={t('form.titlePlaceholder')}
                    maxLength={100}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)} />
            </Column>
            <Column spacing={12} fullWidth>
                <Text color={colors.gray7} fontWeight='bold' size='lg'>{t('form.content')}</Text>
                <TextField placeholder={t('form.contentPlaceholder')}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    as='textarea'
                    maxLength={200}
                    showLength
                    rows={4}
                />
            </Column>
            <Column spacing={12} fullWidth>
                <Text color={colors.gray7} fontWeight='bold' size='lg'>{t('form.image')}</Text>
                <ImageUploader caption={t('form.imageCaption')}
                    value={fileUrls}
                    setValue={setFileUrls}
                    maxLength={2}
                    placeholder={t('form.imagePlaceholder')} />
            </Column>
            <Column fullWidth>
                <Checkbox isChecked={privacy}
                    onClick={() => setPrivacy(!privacy)} >
                    {t('form.privacyAgreement')}
                </Checkbox>
                {termCategories?.map((termCategory) => (
                    <Row fullWidth justify='flex-end' key={termCategory.id}>
                        <Link activityName='TermDetailActivity'
                            activityParams={{ termCategoryId: termCategory.id }}>
                            <TermText color={colors.gray6}
                                fontWeight='bold' >
                                {termCategory.title} {t('form.termSuffix')}
                            </TermText>
                        </Link>
                    </Row>
                ))}
            </Column>
        </Container>
    );
}

const Container = styled(Column)`
    padding: 18px;
    min-height: calc(100vh - 205px);
`;

const TermText = styled(Text)`
    padding: 8px;
`;

export default InquiryFormContents;
